import React, { useState, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Badge, Tooltip, theme as antdTheme, Grid, Drawer, Button, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useAppContext } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import {
    UserOutlined,
    MenuOutlined,
    LogoutOutlined,
    ArrowLeftOutlined,
    ContainerOutlined,
    SettingOutlined,
    FileOutlined,
    SendOutlined
} from '@ant-design/icons';

import { usePurchaseOrderAccess } from '../context/PurchaseOrderAccessContext';

const { Sider } = Layout;
const { useBreakpoint } = Grid;

interface SidebarProps {
    collapsible: boolean;
    isMobile?: boolean;
    isSmallDesktop?: boolean;
}

const PurchaseOrderNavigation = React.memo(() => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const isSmallDesktop = screens.md && !screens.lg; // 768px to 1200px
    return <Sidebar collapsible={!isMobile} isMobile={isMobile} isSmallDesktop={isSmallDesktop} />;
});

const Sidebar = React.memo(({ collapsible, isMobile, isSmallDesktop }: SidebarProps) => {
    const location = useLocation();
    const { state: stateApp, appContextAction } = useAppContext();
    const { isNavMenuClose } = stateApp;
    const { navMenu } = appContextAction;
    const { theme } = useTheme();
    const { user, logout } = useAuth();
    const isAdmin = Boolean((user as any)?.isadmin);
    const isDarkMode = theme.algorithm?.[0] === antdTheme.darkAlgorithm;
    const { access } = usePurchaseOrderAccess();

    const {
        readAccess: canRead = false
    } = access ?? {};
    
    const [drawerVisible, setDrawerVisible] = useState(false);

    const getThemeStyles = useMemo(() => ({
        sidebarHeader: {
            padding: isNavMenuClose && !isMobile ? '8px' : '0 16px',
            display: 'flex',
            flexDirection: isNavMenuClose && !isMobile ? 'column' as const : 'row' as const,
            alignItems: 'center',
            justifyContent: isNavMenuClose && !isMobile ? 'center' : 'space-between',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            borderBottom: 'none',
            backgroundColor: 'transparent'
        },
        logoContainer: {
            display: 'flex',
            alignItems: 'center',
            paddingBottom: isNavMenuClose && !isMobile ? '8px' : '0',
            justifyContent: isNavMenuClose && !isMobile ? 'center' : 'flex-start',
            marginBottom: isNavMenuClose && !isMobile ? '8px' : '0'
        },
        logo: {
            width: isNavMenuClose && !isMobile ? '50px' : (isSmallDesktop ? '180px' : '200px'),
            height: 'auto',
            transition: 'width 0.3s ease'
        },
        toggleButton: {
            color: isDarkMode ? '#fff' : '#000',
            background: 'transparent',
            marginTop: isNavMenuClose && !isMobile ? '12px' : undefined
        },
        userEmail: {
            fontSize: '12px',
            opacity: 0.85,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)'
        },
        collapsedProfile: {
            padding: '16px 0',
            display: 'flex',
            justifyContent: 'center',
            borderBottom: 'none',
            marginBottom: '8px',
            backgroundColor: 'transparent'
        },
        navigationMenu: {
            flex: 1,
            overflowY: 'auto' as const,
            overflowX: 'hidden' as const,
            padding: '0',
            height: 'auto',
            minHeight: 0
        },
        sidebarStyle: {
            boxShadow: 'none',
            zIndex: 1000,
            height: '100vh',
            position: 'fixed' as const,
            left: 0,
            display: 'flex',
            flexDirection: 'column' as const,
            transition: 'all 0.2s ease',
            background: 'transparent',
            backgroundColor: 'transparent',
            overflow: 'hidden',
            ...(isSmallDesktop && !isNavMenuClose && {
                width: '220px !important'
            })
        }
    }), [isDarkMode, isNavMenuClose, isMobile, isSmallDesktop]);

    const currentPath = useMemo(() => {
        const path = location.pathname;
        
        if (path === '/purchase-order/orders') return 'purchase-orders';
        if (path === '/purchase-order/acknowledged-pos') return 'acknowledged-pos';
        if (path === '/purchase-order/orders/new') return 'purchase-orders';
        if (path === '/purchase-order/orders/:orderId/edit') return 'purchase-orders';
        if (path === '/purchase-order/orders/:orderId/view') return 'purchase-orders';
        if (path.startsWith('/purchase-order/orders/')) return 'purchase-orders';
        if (path === '/purchase-order/dispatch') return 'dispatch';
        if (path === '/purchase-order/settings') return 'settings';
        if (path === '/purchase-order/vendor-email-status') return 'vendor-email-status';
        if (path === '/purchase-order/exports') return 'exports';

        const segments = path.split('/').filter(Boolean);
        return segments.length > 0 ? segments[segments.length - 1] : '';
    }, [location.pathname]);

    const menuItems: MenuProps['items'] = useMemo(() => {
        const items: MenuProps['items'] = [];

        if (canRead) {
            items.push({
                key: 'purchase-orders',
                icon: <ContainerOutlined />,
                label: <Link to="/purchase-order/orders">Purchase Orders</Link>
            });
        }                

        if (canRead) {
            items.push({
                key: 'acknowledged-pos',
                icon: <FileOutlined />,
                label: <Link to="/purchase-order/acknowledged-pos">Acknowledged POs</Link>
            });
        }

        if (canRead) {
            items.push({
                key: 'terms-and-conditions',
                icon: <FileOutlined />,
                label: <Link to="/purchase-order/terms-and-conditions">Terms & Conditions</Link>
            });
        }

        if (canRead) {
            items.push({
                key: 'dispatch',
                icon: <SendOutlined />,
                label: <Link to="/purchase-order/dispatch">PO Dispatch</Link>
            });
        }

        if (canRead) {
            items.push({
                key: 'settings',
                icon: <SettingOutlined />,
                label: <Link to="/purchase-order/settings">Settings</Link>
            });
        }

        if (canRead) {
            items.push({
                key: 'exports',
                icon: <FileOutlined />,
                label: <Link to="/purchase-order/exports">PO Exports</Link>
            });
        }

        if (isAdmin) {
            items.push({
                key: 'vendor-email-status',
                icon: <SettingOutlined />,
                label: <Link to="/purchase-order/vendor-email-status">Vendor Email Status</Link>
            });
        }

        items.push({ type: 'divider' });

        items.push({ key: 'back-to-stages', icon: <ArrowLeftOutlined />, label: <Link to="/project-selection">Back to Previous</Link> });

        items.push({ key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: logout, danger: true });
        return items;
    }, [canRead, isAdmin, logout]);

    const toggleCollapse = useCallback(() => {
        if (isNavMenuClose) {
            navMenu.expand();
        } else {
            navMenu.collapse();
        }
    }, [isNavMenuClose, navMenu]);

    const handleMenuClick = useCallback((info: any) => {
        if (info.key === 'logout') {
            logout();
        }

        if (isMobile) {
            setDrawerVisible(false);
        }
    }, [logout, isMobile]);

    const toggleDrawer = useCallback(() => {
        setDrawerVisible(prev => !prev);
    }, []);

    const SidebarContent = useMemo(() => (
        <div className="transparent-nav" style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <div className="sidebar-header" style={getThemeStyles.sidebarHeader}>
                <div className="logo-container" style={getThemeStyles.logoContainer}>
                    {isNavMenuClose && !isMobile ? (
                        <div style={{
                            width: 42,
                            height: 42,
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #1d8348 0%, #145a32 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            margin: '12px 0',
                        }}>
                            <Typography.Text style={{
                                color: '#ffffff',
                                fontWeight: 700,
                                fontSize: '16px',
                                lineHeight: 1,
                                letterSpacing: '-0.5px',
                            }}>
                                CC
                            </Typography.Text>
                        </div>
                    ) : (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '16px 0',
                        }}>
                            <div style={{
                                width: 40,
                                height: 40,
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #1d8348 0%, #145a32 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                <Typography.Text style={{
                                    color: '#ffffff',
                                    fontWeight: 700,
                                    fontSize: '16px',
                                    lineHeight: 1,
                                    letterSpacing: '-0.5px',
                                }}>
                                    CC
                                </Typography.Text>
                            </div>
                            <div style={{ overflow: 'hidden' }}>
                                <Typography.Text style={{
                                    display: 'block',
                                    fontWeight: 700,
                                    fontSize: isSmallDesktop ? '13px' : '14px',
                                    color: isDarkMode ? '#ffffff' : '#1a1a1a',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    lineHeight: 1.3,
                                }}>
                                    Climate Connections
                                </Typography.Text>
                                <Typography.Text style={{
                                    display: 'block',
                                    fontWeight: 500,
                                    fontSize: '12px',
                                    color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.3,
                                    marginTop: '2px',
                                }}>
                                    LLP
                                </Typography.Text>
                            </div>
                        </div>
                    )}
                </div>
            </div>            

            {isNavMenuClose && !isMobile && (
                <div style={getThemeStyles.collapsedProfile}>
                    <Tooltip title={user?.name || 'User'} placement="right">
                        <Badge dot status="success" offset={[-4, 28]}>
                            <Avatar
                                icon={<UserOutlined />}
                                size={36}
                            />
                        </Badge>
                    </Tooltip>
                </div>
            )}

            <div className="navigation-menu" style={getThemeStyles.navigationMenu}>
                <Menu
                    mode="inline"
                    selectedKeys={[currentPath]}
                    defaultOpenKeys={isMobile ? ['main', 'hr', 'settings'] : []}
                    items={menuItems}
                    onClick={handleMenuClick}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        height: '100%'
                    }}
                    theme={isDarkMode ? 'dark' : 'light'}
                />
            </div>
        </div>
    ), [
        getThemeStyles,
        isNavMenuClose,
        isMobile,
        isSmallDesktop,
        user,
        currentPath,
        menuItems,
        handleMenuClick,
        isDarkMode
    ]);

    if (isMobile) {
        return (
            <>
                <Button
                    type="primary"
                    shape="circle"
                    icon={<MenuOutlined />}
                    onClick={toggleDrawer}
                    style={{
                        position: 'fixed',
                        top: '16px',
                        left: '16px',
                        zIndex: 999,
                        boxShadow: '0 3px 6px rgba(0,0,0,0.2)'
                    }}
                    size="middle"
                />
                <Drawer
                    placement="left"
                    onClose={toggleDrawer}
                    open={drawerVisible}
                    width={320}
                    styles={{
                        body: {
                            padding: 0,
                            height: '100%',
                            background: 'transparent',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }
                    }}
                    closable={false}
                >
                    {SidebarContent}
                </Drawer>
            </>
        );
    }

    return (
        <Sider
            collapsible
            collapsed={isNavMenuClose}
            onCollapse={toggleCollapse}
            className="navigation-sidebar transparent-nav"
            theme={isDarkMode ? 'dark' : 'light'}
            width={isSmallDesktop ? 240 : 220}
            collapsedWidth={80}
            // Ensure inline style forces transparency over antd default background
            style={getThemeStyles.sidebarStyle}
            trigger={null}
        >
            {SidebarContent}
        </Sider>
    );
});

export default PurchaseOrderNavigation;
